import { useState, type JSX } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, upload } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { confirmAction, haptic, alertMessage } from '../lib/telegram.js';
import { Adf } from '../components/Adf.js';
import { Card, Empty, ErrorBox, Filter, Ltr, Row, Spinner, StatusChip } from '../components/ui.js';
import { DynamicField } from '../components/DynamicField.js';
import type { FieldDescriptor, IssueCapabilities, IssueDetail } from '../lib/types.js';

interface DetailResponse {
  issue: IssueDetail;
  descriptionHtml: string;
  capabilities: IssueCapabilities;
  muted: boolean;
}

type Tab = 'overview' | 'comments' | 'worklogs' | 'history' | 'edit';

export function IssueDetailPage(): JSX.Element {
  const { issueKey = '' } = useParams();
  const { t } = useI18n();
  const client = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');

  const { data, isLoading, error } = useQuery({
    queryKey: ['issue', issueKey],
    queryFn: () => api<DetailResponse>(`/issues/${issueKey}`),
    enabled: issueKey.length > 0,
  });

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['issue', issueKey] });
  };

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <div className="page">
        <ErrorBox error={error} />
      </div>
    );
  }
  if (!data) return <Empty />;

  const { issue, capabilities } = data;

  const tabs: Array<[Tab, string]> = [
    ['overview', t('issue.overview')],
    ['comments', `💬 ${t('issue.comments')}${issue.commentCount > 0 ? ` (${issue.commentCount})` : ''}`],
    ['worklogs', `⏱ ${t('issue.worklogs')}${issue.worklogCount > 0 ? ` (${issue.worklogCount})` : ''}`],
    ['history', `🕘 ${t('issue.history')}`],
  ];
  if (capabilities.canEdit) tabs.push(['edit', t('common.edit')]);

  return (
    <div className="page">
      <div className="card">
        <div className="header">
          <h1>
            <Ltr>{issue.key}</Ltr>
          </h1>
          <StatusChip name={issue.statusName} category={issue.statusCategoryKey} />
        </div>
        <p style={{ margin: '6px 0 10px', fontWeight: 500 }}>{issue.summary}</p>
        <div className="btn-row">
          <span className="chip">{issue.issueTypeName}</span>
          {issue.priorityName ? <span className="chip">{issue.priorityName}</span> : null}
          {issue.securityLevel ? (
            <span className="chip chip-danger">🔒 {issue.securityLevel.name}</span>
          ) : null}
          {issue.sprintName ? <span className="chip">{issue.sprintName}</span> : null}
        </div>
      </div>

      <ActionBar
        issueKey={issue.key}
        issue={issue}
        capabilities={capabilities}
        muted={data.muted}
        onChanged={invalidate}
      />

      <div className="filter-row">
        {tabs.map(([key, label]) => (
          <Filter key={key} label={label} active={tab === key} onClick={() => setTab(key)} />
        ))}
      </div>

      {tab === 'overview' ? <Overview issue={issue} /> : null}
      {tab === 'comments' ? (
        <Comments issueKey={issue.key} canComment={capabilities.canComment} onChanged={invalidate} />
      ) : null}
      {tab === 'worklogs' ? (
        <Worklogs issueKey={issue.key} canLog={capabilities.canLogWork} onChanged={invalidate} />
      ) : null}
      {tab === 'history' ? <History issueKey={issue.key} /> : null}
      {tab === 'edit' ? <EditForm issueKey={issue.key} onSaved={invalidate} /> : null}

      <a className="btn btn-secondary btn-block" href={issue.url} target="_blank" rel="noreferrer">
        {t('common.open_in_jira')}
      </a>
    </div>
  );
}

function Overview({ issue }: { issue: IssueDetail }): JSX.Element {
  const { t } = useI18n();
  const hasDescription =
    issue.description !== null &&
    issue.description !== undefined &&
    (typeof issue.description !== 'string' || issue.description.trim().length > 0);

  return (
    <>
      <Card title={t('issue.details')}>
        <Row
          title={t('issue.status')}
          right={<StatusChip name={issue.statusName} category={issue.statusCategoryKey} />}
        />
        <Row title={t('issue.assignee')} right={issue.assignee?.displayName ?? t('common.unassigned')} />
        <Row title={t('issue.reporter')} right={issue.reporter?.displayName ?? '—'} />
        <Row title={t('issue.type')} right={issue.issueTypeName} />
        {issue.priorityName ? (
          <Row title={t('issue.priority')} right={issue.priorityName} />
        ) : null}
        <Row
          title={t('issue.project')}
          right={
            <>
              <Ltr>{issue.projectKey}</Ltr>
              {issue.projectName ? ` · ${issue.projectName}` : ''}
            </>
          }
        />
        {issue.parentKey ? (
          <Row title={t('issue.parent')} right={<Ltr>{issue.parentKey}</Ltr>} />
        ) : null}
        {issue.dueDate ? <Row title={t('issue.due')} right={issue.dueDate} /> : null}
        {issue.resolutionName ? (
          <Row title={t('issue.resolution')} right={issue.resolutionName} />
        ) : null}
        {issue.labels.length > 0 ? (
          <Row title={t('issue.labels')} right={issue.labels.join('، ')} />
        ) : null}
        {issue.components.length > 0 ? (
          <Row title={t('issue.components')} right={issue.components.map((c) => c.name).join('، ')} />
        ) : null}
        {issue.fixVersions.length > 0 ? (
          <Row
            title={t('issue.fix_versions')}
            right={issue.fixVersions.map((v) => v.name).join('، ')}
          />
        ) : null}
        {issue.storyPoints !== null ? (
          <Row title={t('issue.story_points')} right={issue.storyPoints} />
        ) : null}
      </Card>

      <Card title={t('issue.description')}>
        {hasDescription ? (
          <Adf doc={issue.description} />
        ) : (
          <Empty text={t('issue.no_description')} />
        )}
      </Card>

      {issue.subtasks.length > 0 ? (
        <Card title={t('issue.subtasks')}>
          {issue.subtasks.map((subtask) => (
            <Row
              key={subtask.key}
              title={
                <>
                  <Ltr>{subtask.key}</Ltr> · {subtask.summary}
                </>
              }
              right={<StatusChip name={subtask.statusName} category={subtask.statusCategoryKey} />}
            />
          ))}
        </Card>
      ) : null}
    </>
  );
}

function ActionBar({
  issueKey,
  issue,
  capabilities,
  muted,
  onChanged,
}: {
  issueKey: string;
  issue: IssueDetail;
  capabilities: IssueCapabilities;
  muted: boolean;
  onChanged: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [showTransitions, setShowTransitions] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      haptic('success');
      onChanged();
    } catch (error) {
      haptic('error');
      alertMessage(error instanceof Error ? error.message : t('error.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="btn-row">
          {capabilities.canTransition ? (
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => setShowTransitions((v) => !v)}
            >
              {t('action.transition')}
            </button>
          ) : null}
          {capabilities.canAssign ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => setShowAssign((v) => !v)}
            >
              {t('action.assign')}
            </button>
          ) : null}
          {capabilities.canBeAssignedToMe ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const me = await api<{ accountId: string }>('/me/jira');
                  await api(`/issues/${issueKey}/assignee`, {
                    method: 'PUT',
                    body: { accountId: me.accountId },
                  });
                })
              }
            >
              {t('action.assign_to_me')}
            </button>
          ) : null}
          {capabilities.canManageWatchers ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() =>
                void run(() =>
                  api(`/issues/${issueKey}/watchers`, {
                    method: issue.isWatching ? 'DELETE' : 'POST',
                    body: {},
                  }),
                )
              }
            >
              {issue.isWatching ? t('action.unwatch') : t('action.watch')}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() =>
              void run(() =>
                api(`/issues/${issueKey}/mute`, {
                  method: muted ? 'DELETE' : 'POST',
                  body: { minutes: null },
                }),
              )
            }
          >
            {muted ? t('action.unmute') : t('action.mute')}
          </button>
          {capabilities.canAttach ? <AttachButton issueKey={issueKey} onChanged={onChanged} /> : null}
          {capabilities.canDelete ? (
            <DeleteButton issueKey={issueKey} summary={issue.summary} />
          ) : null}
        </div>
      </div>

      {showTransitions ? (
        <Card title={t('transition.pick', { issue: issueKey }).replace(/<[^>]+>/g, '')}>
          {capabilities.availableTransitions.length === 0 ? (
            <Empty text={t('transition.none')} />
          ) : (
            capabilities.availableTransitions.map((transition) => (
              <Row
                key={transition.id}
                onClick={() =>
                  void run(async () => {
                    await api(`/issues/${issueKey}/transitions`, {
                      method: 'POST',
                      body: { transitionId: transition.id },
                    });
                    setShowTransitions(false);
                  })
                }
                title={transition.name}
                subtitle={transition.toStatusName}
                right={
                  transition.requiredFields.length > 0 ? (
                    <span className="chip">
                      {t('common.fields_count', { count: transition.requiredFields.length })}
                    </span>
                  ) : null
                }
              />
            ))
          )}
        </Card>
      ) : null}

      {showAssign ? (
        <AssignPanel
          issueKey={issueKey}
          onDone={() => {
            setShowAssign(false);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function AssignPanel({
  issueKey,
  onDone,
}: {
  issueKey: string;
  onDone: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const [query, setQuery] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['assignable', issueKey, query],
    queryFn: () =>
      api<{
        users: Array<{
          accountId: string;
          displayName: string;
          linked: boolean;
          isMe: boolean;
        }>;
      }>(`/issues/${issueKey}/assignable${query ? `?q=${encodeURIComponent(query)}` : ''}`),
  });

  const assign = useMutation({
    mutationFn: (accountId: string | null) =>
      api(`/issues/${issueKey}/assignee`, { method: 'PUT', body: { accountId } }),
    onSuccess: onDone,
  });

  return (
    <Card title={t('assign.pick')}>
      <div className="field">
        <input
          value={query}
          placeholder={t('assign.search')}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {isLoading ? <Spinner /> : null}
      {error ? <ErrorBox error={error} /> : null}
      {(data?.users ?? []).length === 0 && !isLoading ? (
        <Empty text={t('assign.none_assignable')} />
      ) : null}
      {(data?.users ?? []).map((user) => (
        <Row
          key={user.accountId}
          onClick={() => assign.mutate(user.accountId)}
          title={user.isMe ? `👤 ${t('assign.me')}` : user.displayName}
          subtitle={user.linked ? t('assign.teammates') : t('assign.others')}
        />
      ))}
      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={() => assign.mutate(null)}
      >
        {t('action.unassign')}
      </button>
      {assign.error ? <ErrorBox error={assign.error} /> : null}
    </Card>
  );
}

function AttachButton({
  issueKey,
  onChanged,
}: {
  issueKey: string;
  onChanged: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  return (
    <label className="btn btn-secondary" style={{ margin: 0 }}>
      {busy ? '…' : t('action.attach')}
      <input
        type="file"
        style={{ display: 'none' }}
        onChange={async (event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          setBusy(true);
          try {
            await upload(`/issues/${issueKey}/attachments`, file);
            haptic('success');
            onChanged();
          } catch (error) {
            haptic('error');
            alertMessage(error instanceof Error ? error.message : t('error.generic'));
          } finally {
            setBusy(false);
            event.target.value = '';
          }
        }}
      />
    </label>
  );
}

/**
 * Destructive delete: a server-issued, single-use confirmation token bound to
 * this exact issue is fetched first, then spent on the delete call.
 */
function DeleteButton({ issueKey, summary }: { issueKey: string; summary: string }): JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  return (
    <button
      type="button"
      className="btn btn-danger"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const confirmation = await api<{
            confirmationId: string;
            effect: string;
            subtaskCount: number;
          }>(`/issues/${issueKey}/confirm-delete`, { method: 'POST' });

          const ok = await confirmAction(
            `${t('confirm.destructive')}\n\n${issueKey} — ${summary}\n\n${confirmation.effect}`,
          );
          if (!ok) return;

          await api(`/issues/${issueKey}`, {
            method: 'DELETE',
            body: {
              confirmationId: confirmation.confirmationId,
              deleteSubtasks: confirmation.subtaskCount > 0,
            },
          });
          haptic('success');
          window.history.back();
        } catch (error) {
          haptic('error');
          alertMessage(error instanceof Error ? error.message : t('error.generic'));
        } finally {
          setBusy(false);
        }
      }}
    >
      🗑 {t('action.delete')}
    </button>
  );
}

function Comments({
  issueKey,
  canComment,
  onChanged,
}: {
  issueKey: string;
  canComment: boolean;
  onChanged: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const client = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['comments', issueKey],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          author: { displayName: string } | null;
          body: unknown;
          created: string;
          restrictedTo: string | null;
          canEdit: boolean;
          canDelete: boolean;
        }>;
      }>(`/issues/${issueKey}/comments`),
  });

  const add = useMutation({
    mutationFn: () => api(`/issues/${issueKey}/comments`, { method: 'POST', body: { text } }),
    onSuccess: () => {
      setText('');
      void client.invalidateQueries({ queryKey: ['comments', issueKey] });
      onChanged();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/issues/${issueKey}/comments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['comments', issueKey] });
      onChanged();
    },
  });

  return (
    <>
      {canComment ? (
        <Card>
          <div className="field">
            <textarea
              value={text}
              placeholder={t('comment.prompt', { issue: issueKey }).replace(/<[^>]+>/g, '')}
              onChange={(event) => setText(event.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-block"
            disabled={text.trim().length === 0 || add.isPending}
            onClick={() => add.mutate()}
          >
            {t('action.comment')}
          </button>
          {add.error ? <ErrorBox error={add.error} /> : null}
        </Card>
      ) : null}

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorBox error={error} /> : null}

      <Card>
        {(data?.items ?? []).length === 0 ? (
          <Empty text={t('issue.no_comments')} />
        ) : (
          (data?.items ?? []).map((comment) => (
            <div key={comment.id} className="row" style={{ display: 'block' }}>
              <div className="row-sub">
                <strong>{comment.author?.displayName ?? '—'}</strong>{' '}
                {new Date(comment.created).toLocaleString()}
                {comment.restrictedTo ? (
                  <span className="chip chip-danger" style={{ marginInlineStart: 6 }}>
                    🔒 {comment.restrictedTo}
                  </span>
                ) : null}
              </div>
              <Adf doc={comment.body} />
              {comment.canDelete ? (
                <button
                  type="button"
                  className="btn btn-secondary small"
                  onClick={async () => {
                    if (await confirmAction(t('confirm.destructive'))) remove.mutate(comment.id);
                  }}
                >
                  {t('common.delete')}
                </button>
              ) : null}
            </div>
          ))
        )}
      </Card>
    </>
  );
}

function Worklogs({
  issueKey,
  canLog,
  onChanged,
}: {
  issueKey: string;
  canLog: boolean;
  onChanged: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const [timeSpent, setTimeSpent] = useState('1h');
  const [comment, setComment] = useState('');
  const client = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['worklogs', issueKey],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          author: { displayName: string } | null;
          timeSpent: string | null;
          started: string;
          isMine: boolean;
        }>;
      }>(`/issues/${issueKey}/worklogs`),
  });

  const add = useMutation({
    mutationFn: () =>
      api(`/issues/${issueKey}/worklogs`, {
        method: 'POST',
        body: { timeSpent, ...(comment ? { comment } : {}) },
      }),
    onSuccess: () => {
      setComment('');
      void client.invalidateQueries({ queryKey: ['worklogs', issueKey] });
      onChanged();
    },
  });

  return (
    <>
      {canLog ? (
        <Card>
          <div className="btn-row" style={{ marginBottom: 10 }}>
            {['15m', '30m', '1h', '2h'].map((preset) => (
              <Filter
                key={preset}
                label={preset}
                active={timeSpent === preset}
                onClick={() => setTimeSpent(preset)}
              />
            ))}
          </div>
          <div className="field">
            <label htmlFor="time">{t('worklog.custom')}</label>
            <input id="time" value={timeSpent} onChange={(e) => setTimeSpent(e.target.value)} />
          </div>
          <div className="field">
            <textarea
              value={comment}
              placeholder={t('action.comment')}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-block"
            disabled={add.isPending}
            onClick={() => add.mutate()}
          >
            {t('action.log_work')}
          </button>
          {add.error ? <ErrorBox error={add.error} /> : null}
        </Card>
      ) : null}

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorBox error={error} /> : null}

      <Card>
        {(data?.items ?? []).length === 0 ? (
          <Empty text={t('issue.no_worklogs')} />
        ) : (
          (data?.items ?? []).map((worklog) => (
            <Row
              key={worklog.id}
              title={worklog.author?.displayName ?? '—'}
              subtitle={new Date(worklog.started).toLocaleString()}
              right={<span className="chip">{worklog.timeSpent ?? ''}</span>}
            />
          ))
        )}
      </Card>
    </>
  );
}

function History({ issueKey }: { issueKey: string }): JSX.Element {
  const { t } = useI18n();
  const { data, isLoading, error } = useQuery({
    queryKey: ['changelog', issueKey],
    queryFn: () =>
      api<{
        histories: Array<{
          id: string;
          author?: { displayName: string };
          created: string;
          items: Array<{ field: string; fromString: string | null; toString: string | null }>;
        }>;
      }>(`/issues/${issueKey}/changelog`),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  return (
    <Card>
      {(data?.histories ?? []).length === 0 ? (
        <Empty text={t('issue.no_history')} />
      ) : (
        (data?.histories ?? []).map((entry) => (
          <div key={entry.id} className="row" style={{ display: 'block' }}>
            <div className="row-sub">
              <strong>{entry.author?.displayName ?? '—'}</strong>{' '}
              {new Date(entry.created).toLocaleString()}
            </div>
            {entry.items.map((item, index) => (
              <div key={index} className="small">
                <strong>{item.field}</strong>: {item.fromString ?? '—'} → {item.toString ?? '—'}
              </div>
            ))}
          </div>
        ))
      )}
    </Card>
  );
}

function EditForm({
  issueKey,
  onSaved,
}: {
  issueKey: string;
  onSaved: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const [values, setValues] = useState<Record<string, unknown>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['editmeta', issueKey],
    queryFn: () => api<{ fields: FieldDescriptor[] }>(`/issues/${issueKey}/editmeta`),
  });

  const save = useMutation({
    mutationFn: () => api(`/issues/${issueKey}`, { method: 'PATCH', body: { fields: values } }),
    onSuccess: () => {
      setValues({});
      onSaved();
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const editable = (data?.fields ?? []).filter((field) => field.kind !== 'unsupported');

  return (
    <Card title={t('common.edit')}>
      {editable.length === 0 ? <Empty /> : null}
      {editable.map((field) => (
        <DynamicField
          key={field.fieldId}
          field={field}
          value={values[field.fieldId]}
          onChange={(next) => setValues((prev) => ({ ...prev, [field.fieldId]: next }))}
        />
      ))}
      <button
        type="button"
        className="btn btn-block"
        disabled={Object.keys(values).length === 0 || save.isPending}
        onClick={() => save.mutate()}
      >
        {t('common.save')}
      </button>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </Card>
  );
}
